// Hire a new employee (prototype scene 22): 1. job, 2. name and look, 3. tools. The new employee then walks in
// from the door to the free desk. Tools that change data always ask the boss first.
import { useState } from 'react';
import Avatar from '../Avatar';
import { hireAgent, errorText } from '../api';

// Starting points with real company tools. Everything can be changed later on the profile page.
const ROLES = [
  { t: 'Purchase & store', d: 'Tracks paper, glue and ink stock. Raises purchase entries.',
    read: ['get_all_stock_items', 'get_stock_item', 'get_purchase_vouchers'], write: ['create_purchase_voucher'] },
  { t: 'Sales follow-up', d: 'Follows up orders and dues, and gives staff tasks to chase payment.',
    read: ['list_orders', 'get_order_details', 'get_top_exposures', 'get_sales_register'], write: ['create_task'] },
  { t: 'HR & staff tasks', d: 'Keeps track of staff and their tasks, and chases what is overdue.',
    read: ['list_users', 'list_departments', 'list_tasks', 'get_task_details'], write: ['create_task', 'add_task_comment'] },
  { t: 'Start from blank', d: 'Write the job yourself and pick the tools later on the profile page.', read: [], write: [] },
];
const SHIRTS = ['#2D9C9C', '#D9485F', '#2F6FB0', '#E3A33B', '#7A55B5', '#3E9C6E'];
const HAIRS = ['#2B1B12', '#1A1A1A', '#6B4A2E', '#9A9A9A'];
const SKINS = ['#C48A60', '#A8714A', '#D2A07A', '#8A5A3C'];
const VOICES = { female_warm: 'Female · warm', female_clear: 'Female · clear', male_calm: 'Male · calm' };
const MODELS = { 'claude-haiku-4-5': 'Fast and cheap', 'claude-sonnet-5-5': 'Smart' };
// Same rule as the server: letters (Bangla too), spaces and . ' - only, up to 30.
const NAME_RE = /^[\p{L}][\p{L}\p{M} .'-]{0,29}$/u;

export default function Hire({ onHired, onCancel }) {
  const [step, setStep] = useState(0);
  const [roleIdx, setRoleIdx] = useState(0);
  const [roleName, setRoleName] = useState(ROLES[0].t);
  const [desc, setDesc] = useState(ROLES[0].d);
  const [name, setName] = useState('Rumana');
  const [look, setLook] = useState({ shirt: SHIRTS[0], hair: HAIRS[0], skin: SKINS[0], long: true });
  const [voice, setVoice] = useState('female_warm');
  const [model, setModel] = useState('claude-haiku-4-5');
  const [off, setOff] = useState(new Set()); // tools switched off
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const role = ROLES[roleIdx];
  const toolList = [...role.read.map((t) => [t, 'Looks up data']), ...role.write.map((t) => [t, 'Changes data · asks you first'])];

  const pickRole = (i) => {
    setRoleIdx(i); setOff(new Set());
    setRoleName(i === ROLES.length - 1 ? '' : ROLES[i].t);
    setDesc(i === ROLES.length - 1 ? '' : ROLES[i].d);
  };

  const hire = async () => {
    setSaving(true); setError(null);
    try {
      const tools = toolList.map(([t]) => t).filter((t) => !off.has(t));
      const r = await hireAgent({ name: name.trim(), role: roleName.trim(), description: desc.trim(), instructions: desc.trim(), look, voice, model, tools });
      onHired(r.id, name.trim());
    } catch (err) {
      const msg = errorText(err);
      if (/name|colour/i.test(msg)) setStep(1); // back to the Name and look step
      setError(msg);
      setSaving(false);
    }
  };

  const next = () => {
    setError(null);
    if (step === 0 && !roleName.trim()) return setError('Write the job name.');
    if (step === 1 && !name.trim()) return setError('Give them a name.');
    if (step === 1 && (name.trim().length < 2 || !NAME_RE.test(name.trim()))) return setError('Use 2 to 30 letters for the name. Spaces and . \' - are fine, numbers are not.');
    if (step < 2) setStep(step + 1); else hire();
  };

  const swatch = (list, key, label) => (
    <div className="sw">
      {list.map((c) => <button key={c} type="button" className={look[key] === c ? 'on' : ''} style={{ background: c }} aria-label={`${label} colour`} onClick={() => setLook((l) => ({ ...l, [key]: c }))} />)}
    </div>
  );

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="vh"><div><h2>Hire a new employee</h2><p>They will sit at the free desk.</p></div></div>
      <div className="steps">{['1. Job', '2. Name and look', '3. Tools'].map((s, i) => <span key={s} className={i === step ? 'on' : ''}>{s}</span>)}</div>

      {step === 0 && (
        <>
          <div className="roles">
            {ROLES.map((x, i) => <button key={x.t} type="button" className={i === roleIdx ? 'on' : ''} onClick={() => pickRole(i)}><b>{x.t}</b><span>{x.d}</span></button>)}
          </div>
          <label className="field"><span>Job name</span><input value={roleName} onChange={(e) => setRoleName(e.target.value)} maxLength={60} /></label>
          <label className="field"><span>What they do (their job instructions; you can change it later)</span><textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} /></label>
        </>
      )}

      {step === 1 && (
        <div className="look">
          <Avatar agent={{ look }} size="lg" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 220 }}>
            <label className="field"><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} /></label>
            <div className="field"><span>Shirt</span>{swatch(SHIRTS, 'shirt', 'Shirt')}</div>
            <div className="field"><span>Hair</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{swatch(HAIRS, 'hair', 'Hair')}
                <button type="button" className="aio-btn" style={{ height: 30, padding: '0 10px' }} aria-pressed={!!look.long} onClick={() => setLook((l) => ({ ...l, long: !l.long }))}>{look.long ? 'Long hair' : 'Short hair'}</button>
              </div>
            </div>
            <div className="field"><span>Skin</span>{swatch(SKINS, 'skin', 'Skin')}</div>
            <label className="field"><span>Voice</span>
              <select value={voice} onChange={(e) => setVoice(e.target.value)}>{Object.entries(VOICES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </label>
            <label className="field"><span>AI model</span>
              <select value={model} onChange={(e) => setModel(e.target.value)}>{Object.entries(MODELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </label>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <p style={{ margin: '0 0 8px', color: 'var(--muted)' }}>Tools that change data always ask you first. You can change all of this on the profile page.</p>
          {toolList.length ? toolList.map(([t, s]) => (
            <div className="trow" key={t}>
              <span><code>{t}</code><small>{s}</small></span>
              <label className="switch"><input type="checkbox" checked={!off.has(t)} aria-label={t}
                onChange={(e) => setOff((o) => { const n = new Set(o); if (e.target.checked) n.delete(t); else n.add(t); return n; })} /><span /></label>
            </div>
          )) : <p>No tools yet. You can add them later on the profile page.</p>}
        </div>
      )}

      {error && <div className="err">{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="aio-btn ghost" onClick={onCancel}>Cancel</button>
        {step > 0 && <button type="button" className="aio-btn" onClick={() => setStep(step - 1)} disabled={saving}>Back</button>}
        <button type="button" className="aio-btn pri" onClick={next} disabled={saving}>{step === 2 ? (saving ? 'Hiring…' : `Hire ${name.trim() || ''}`) : 'Next'}</button>
      </div>
    </div>
  );
}
